import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppLayout } from '@/components/app-layout';
import { RequireAdmin, RequireAuth, RequireSignedIn } from '@/components/require-auth';
import { ToastProvider } from '@/components/ui/toast';
import { AdminFailuresPage } from '@/pages/admin-failures-page';
import { AuthCallbackPage } from '@/pages/auth-callback-page';
import { CataloguePage } from '@/pages/catalogue-page';
import { CategoriesPage } from '@/pages/categories-page';
import { MediaPage } from '@/pages/media-page';
import { LinkDetailPage } from '@/pages/link-detail-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { SettingsPage } from '@/pages/settings-page';
import { StatsPage } from '@/pages/stats-page';
import { AuthProvider } from '@/lib/auth';
import { createQueryClient } from '@/lib/query-client';
import { ThemeProvider } from '@/lib/theme';
import { ROUTER_FUTURE } from '@/lib/router-future';

const queryClient = createQueryClient();

/**
 * `/auth/callback` sits OUTSIDE RequireAuth: the PKCE code exchange has to run before a
 * session exists, so guarding it would bounce the user straight back to sign-in.
 */
const router = createBrowserRouter(
  [
    {
      path: '/auth/callback',
      element: <AuthCallbackPage />,
    },
    {
      element: (
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <CataloguePage /> },
        { path: 'links/:linkId', element: <LinkDetailPage /> },
        {
          // Employees' own files from internal channels: never public, whatever the catalogue is.
          path: 'media',
          element: (
            <RequireSignedIn>
              <MediaPage />
            </RequireSignedIn>
          ),
        },
        {
          path: 'stats',
          element: (
            <RequireSignedIn>
              <StatsPage />
            </RequireSignedIn>
          ),
        },
        {
          path: 'categories',
          element: (
            <RequireAdmin>
              <CategoriesPage />
            </RequireAdmin>
          ),
        },
        {
          path: 'admin/failures',
          element: (
            <RequireAdmin>
              <AdminFailuresPage />
            </RequireAdmin>
          ),
        },
        {
          path: 'settings',
          element: (
            <RequireAdmin>
              <SettingsPage />
            </RequireAdmin>
          ),
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  { future: ROUTER_FUTURE },
);

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
